import { useMemo, useState } from "react";
import {
  ArchiveRestore,
  CircleCheck,
  CirclePause,
  Files,
  Folder,
  ListTodo,
  MoreVertical,
  Pencil,
  Plus,
  Users,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CreateProjectDialog } from "@/domains/projects/components/CreateProjectDialog";
import {
  PROJECT_TILE_CLASS,
  PROJECT_TILE_HEIGHT_CLASS,
  PROJECT_TILE_INTERACTIVE_CLASS,
  PROJECT_TILE_TEXT_INDENT_CLASS,
} from "@/domains/projects/components/projectTile";
import { cn } from "@/lib/utils";
import { useProjects } from "../hooks/useProjects";
import { useOrgProjectsOverview } from "@/hooks/projects/useOrgProjectsOverview";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { OrgProjectOverview } from "@/stores/organizations/repository";
import type { BuildingProject, ProjectStatus } from "@/types/projects";
import { ProjectStatusPill } from "@/domains/projects/status/ProjectStatusPill";
import { ProjectStatusFilter } from "@/domains/projects/status/ProjectStatusFilter";
import { useProjectStatusEnabled } from "@/domains/projects/status/projectStatusAvailability";
import {
  partitionProjectsByStatus,
  type ProjectStatusFilterValue,
} from "@/domains/projects/status/projectStatus";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/** Dashboard grid caps at this many most-recently-edited project tiles (the
 *  grid holds real projects only — creating one is a button on the label row).
 *  When more projects exist, the title row's "See all (N) →" toggle expands the
 *  grid in place (there is no standalone projects page any more). */
const DASHBOARD_PROJECT_LIMIT = 12;

/** The expanded status pill grows leftwards over the tail of the project name;
 *  a ring in the tile's own white masks the letters it lands on. */
const STATUS_PILL_MASK_CLASS =
  "hover:ring-4 hover:ring-white data-[state=open]:ring-4 data-[state=open]:ring-white";

type ProjectCounts = { docCount: number; memberCount: number; issueCount: number | null };

const boxIconClass = "h-5 w-5 shrink-0 text-primary";

/** Tiles share the full row width (1fr tracks, no leftover gap on the right)
 *  and the column count adapts: once another 300px track fits, a column is
 *  added instead of the tiles growing further. min() guards viewports narrower
 *  than one track. */
const gridClass = "grid grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))] gap-3";

/** Box labels are a hard 14px: the root font-size is scaled to 90% (index.css),
 *  so rem-based text-sm would render at ~12.6px, not the 14px the design asks for. */
const boxLabelClass = "text-[14px] font-semibold";

/** [+ New project] — a button on the section label row, not a grid tile:
 *  creating a project is one action and shouldn't cost a whole grid slot. The
 *  grid below holds real projects only. TERTIARY (ghost — no fill, no border)
 *  on the shared control scale, so it lines up with every other action in the
 *  app without competing with the project tiles beside it. */
function NewProjectButton({ onClick }: { onClick: () => void }) {
  const { t } = useLanguage();
  return (
    <Button
      variant="ghost"
      size="control"
      onClick={onClick}
      {...pageInfoTarget("building.newProject")}
    >
      <Plus className="mr-1.5" />
      {t.projects.selector.newProject}
    </Button>
  );
}

/** The doc-count · member-count · issue-count · created-date row under a
 *  project's name. Members include the org's owners and admins (they reach
 *  every project); issues are every live issue, resolved ones included.
 *  `counts` is undefined while the overview RPC is loading or errored, or when
 *  the settled overview has no row for this project (e.g. a just-created
 *  project the 60s-stale overview hasn't seen yet, or an RPC scoping
 *  difference) — render "—" then, so an unknown count never masquerades as a
 *  real 0. Genuinely zero-count projects still get a row from the RPC (the
 *  /projects browsing surfaces list projects from these same rows).
 *  `mt-auto` pins the row to the card's bottom edge (the card body is a
 *  flex column). */
function ProjectCardMeta({ counts, createdAt }: { counts?: ProjectCounts; createdAt: string }) {
  const { t, language } = useLanguage();
  // Day-first ("17 Jul" / "17. juuli") — explicit locale, because the browser
  // default would render month-first for en-US users.
  const date = new Date(createdAt).toLocaleDateString(language === "et" ? "et-EE" : "en-GB", {
    day: "numeric",
    month: "short",
  });
  const dot = <span>·</span>;
  // <span>, not <div>: this renders inside the card's <button>, which only
  // allows phrasing content.
  return (
    <span
      className={cn(
        "mt-auto flex items-center gap-2 pt-1 text-xs text-muted-foreground",
        PROJECT_TILE_TEXT_INDENT_CLASS
      )}
    >
      <span className="inline-flex items-center gap-1" title={t.projects.card.documents}>
        <Files className="h-3.5 w-3.5" />
        {counts ? counts.docCount : "—"}
      </span>
      {dot}
      <span className="inline-flex items-center gap-1" title={t.projects.card.members}>
        <Users className="h-3.5 w-3.5" />
        {counts ? counts.memberCount : "—"}
      </span>
      {dot}
      <span className="inline-flex items-center gap-1" title={t.projects.card.issues}>
        <ListTodo className="h-3.5 w-3.5" />
        {counts?.issueCount ?? "—"}
      </span>
      {dot}
      <span>{date}</span>
    </span>
  );
}

/** The kebab (⋮) menu. Each item carries its own gate — the caller passes a
 *  handler only when the user may use it (Edit: MODIFY_PROJECTS; the status
 *  toggle: MANAGE_PROJECT_STATUS, owners and admins) and hides the menu when it
 *  has neither. Deliberately carries no destructive action — "Delete project"
 *  lives inside the Edit Project dialog (see EditProjectDialog), so it can't be
 *  hit by accident from the card; marking a project inactive only relabels it. */
function ProjectCardMenu({
  status,
  onEdit,
  onSetStatus,
  isSettingStatus,
}: {
  status: ProjectStatus;
  onEdit?: () => void;
  onSetStatus?: (status: ProjectStatus) => void;
  isSettingStatus: boolean;
}) {
  const { t } = useLanguage();
  const setStatus = (nextStatus: ProjectStatus) => {
    if (!isSettingStatus) onSetStatus?.(nextStatus);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground"
          aria-label={t.projects.card.actions}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onEdit && (
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            {t.common.edit}
          </DropdownMenuItem>
        )}
        {onSetStatus &&
          (status === "inactive" ? (
            <DropdownMenuItem disabled={isSettingStatus} onClick={() => setStatus("active")}>
              <CircleCheck className="mr-2 h-4 w-4" />
              {t.projects.status.markActive}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem disabled={isSettingStatus} onClick={() => setStatus("inactive")}>
              <CirclePause className="mr-2 h-4 w-4" />
              {t.projects.status.markInactive}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface DashboardProjectCardProps {
  project: BuildingProject;
  counts?: ProjectCounts;
  onSelect: () => void;
  /** Omit when the user may not edit (MODIFY_PROJECTS). */
  onEdit?: () => void;
  /** Omit when the user may not change the status (MANAGE_PROJECT_STATUS). */
  onSetStatus?: (status: ProjectStatus) => void;
  isSettingStatus: boolean;
}

/** The icon + name row, the optional description and the meta row shared by the
 *  active and archived tiles. Phrasing content only, so it can sit inside the
 *  active tile's <button> as well as the archived tile's inert <span>.
 *
 *  Layout is a fixed-height, top-anchored column: the folder icon and name sit
 *  on the top line (right-padded to clear the ⋮ menu), the meta row is pinned
 *  to the bottom (mt-auto), and the description — when the project has one —
 *  sits in between. The tile height is sized to exactly those three lines, so
 *  the pinned meta row lands directly under the text rather than across a gap,
 *  and names and meta rows share two baselines across every tile. Description
 *  and meta are indented to the title *text*, past the folder icon. */
function ProjectCardBody({
  project,
  counts,
}: {
  project: BuildingProject;
  counts?: ProjectCounts;
}) {
  return (
    <>
      <span className="flex items-center gap-2 pr-12">
        <Folder className={boxIconClass} />
        <span className={`min-w-0 flex-1 truncate leading-snug ${boxLabelClass}`}>
          {project.name}
        </span>
      </span>
      {project.description && (
        <span
          className={cn(
            "mt-1 block truncate text-xs text-muted-foreground",
            PROJECT_TILE_TEXT_INDENT_CLASS
          )}
          title={project.description}
        >
          {project.description}
        </span>
      )}
      <ProjectCardMeta counts={counts} createdAt={project.created_at} />
    </>
  );
}

/** A single project tile. The whole tile is the <button> so any part of it
 *  opens the project; the kebab is an absolutely-positioned sibling on top, so
 *  no interactive element nests inside another (invalid ARIA). */
function DashboardProjectCard({
  project,
  counts,
  onSelect,
  onEdit,
  onSetStatus,
  isSettingStatus,
}: DashboardProjectCardProps) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          PROJECT_TILE_CLASS,
          PROJECT_TILE_INTERACTIVE_CLASS,
          PROJECT_TILE_HEIGHT_CLASS,
          "w-full text-left"
        )}
      >
        <ProjectCardBody project={project} counts={counts} />
      </button>
      {/* Siblings of the tile's <button>, never inside it: the pill is a
          control too. It expands leftwards over the name's tail on hover. */}
      <div className="absolute right-1 top-1.5 flex h-8 items-center gap-0.5">
        <ProjectStatusPill
          status={project.status}
          onSetStatus={onSetStatus}
          busy={isSettingStatus}
          className={STATUS_PILL_MASK_CLASS}
        />
        {(onEdit || onSetStatus) && (
          <ProjectCardMenu
            status={project.status}
            onEdit={onEdit}
            onSetStatus={onSetStatus}
            isSettingStatus={isSettingStatus}
          />
        )}
      </div>
    </div>
  );
}

/** Restore confirmation on an archived card. The AlertDialog step mirrors the
 *  buildings archive UX — restoring is cheap, but it re-surfaces the project in
 *  every picker, so it shouldn't happen on a stray click. */
function ProjectRestoreAction({
  disabled,
  onRestore,
}: {
  disabled: boolean;
  onRestore: () => void;
}) {
  const { t } = useLanguage();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-label={t.projects.archive.restore}
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-primary"
        >
          <ArchiveRestore className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t.projects.archive.restoreTitle}</AlertDialogTitle>
          <AlertDialogDescription>{t.projects.archive.restoreConfirm}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
          <AlertDialogAction onClick={onRestore}>{t.projects.archive.restore}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface ArchivedProjectCardProps {
  project: BuildingProject;
  counts?: ProjectCounts;
  canRestore: boolean;
  restoreDisabled: boolean;
  onRestore: () => void;
}

/** An archived project's tile: the active tile's shell, muted and inert — an
 *  archived project can't be opened (it's excluded from every picker), so the
 *  only affordance is Restore. */
function ArchivedProjectCard({
  project,
  counts,
  canRestore,
  restoreDisabled,
  onRestore,
}: ArchivedProjectCardProps) {
  return (
    <div className="relative">
      <span className={cn(PROJECT_TILE_CLASS, PROJECT_TILE_HEIGHT_CLASS, "opacity-70")}>
        <ProjectCardBody project={project} counts={counts} />
      </span>
      <div className="absolute right-1 top-1.5 flex h-8 items-center gap-0.5">
        <ProjectStatusPill
          status={project.status}
          className={cn(STATUS_PILL_MASK_CLASS, "opacity-70")}
        />
        {canRestore && <ProjectRestoreAction disabled={restoreDisabled} onRestore={onRestore} />}
      </div>
    </div>
  );
}

interface EditProjectFieldsProps {
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
}

/** The name + description form inside the edit dialog. */
function EditProjectFields(props: EditProjectFieldsProps) {
  const { name, setName, description, setDescription } = props;
  const { t } = useLanguage();
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="dashboard-edit-project-name">{t.projects.create.nameLabel}</Label>
        <Input
          id="dashboard-edit-project-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.projects.create.namePlaceholder}
        />
      </div>
      <div>
        <Label htmlFor="dashboard-edit-project-description">
          {t.projects.create.descriptionLabel}
        </Label>
        <Textarea
          id="dashboard-edit-project-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t.projects.create.descriptionPlaceholder}
          rows={3}
        />
      </div>
    </div>
  );
}

/** "Delete project" in the Edit Project dialog footer — a quiet destructive
 *  button with a confirm step. Archives under the hood (product decision:
 *  reversible, data preserved — the project can be brought back from the
 *  dashboard's archived view). */
function ProjectDeleteAction({ disabled, onDelete }: { disabled: boolean; onDelete: () => void }) {
  const { t } = useLanguage();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          {t.projects.archive.deleteAction}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t.projects.archive.deleteTitle}</AlertDialogTitle>
          <AlertDialogDescription>{t.projects.archive.deleteConfirm}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onDelete}
          >
            {t.projects.archive.deleteSubmit}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface EditProjectDialogProps {
  buildingId: string;
  project: BuildingProject;
  onClose: () => void;
}

/** Rename / re-describe a project — and the home of "Delete project" (kept out
 *  of the card so it can't be hit by accident; gated to MODIFY_DATA and never
 *  offered for the building's default project). Mounted only while editing
 *  (keyed by project id), so its form state initialises straight from the
 *  project. */
function EditProjectDialog({ buildingId, project, onClose }: EditProjectDialogProps) {
  const { t } = useLanguage();
  const { updateProject, isUpdating, archiveProject, isArchiving } = useProjects(buildingId);
  const { canArchiveProjects } = usePermissions();
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");

  const handleUpdate = () => {
    if (!name.trim()) return;
    // Close only once the mutation succeeds: on failure the dialog stays
    // open with the user's edits intact (the hook's onError toast reports
    // the failure), so they can retry instead of losing their input.
    updateProject(
      {
        id: project.id,
        name: name.trim(),
        description: description.trim() || undefined,
      },
      { onSuccess: onClose }
    );
  };

  const handleDelete = () => {
    archiveProject({ id: project.id, archived: true }, { onSuccess: onClose });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t.projects.edit.title}</DialogTitle>
        </DialogHeader>
        <EditProjectFields
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
        />
        <div className="flex items-center justify-between gap-2">
          <div>
            {canArchiveProjects && !project.is_default && (
              <ProjectDeleteAction disabled={isArchiving} onDelete={handleDelete} />
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {t.common.cancel}
            </Button>
            <Button onClick={handleUpdate} disabled={!name.trim() || isUpdating}>
              {isUpdating ? t.projects.edit.updating : t.projects.edit.submit}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface DashboardProjectsGridProps {
  buildingId: string;
  /** The building's own org_id — NOT the ambient org-switcher context. A
   * multi-org user can deep-link into a building while a different org is
   * selected in the header; scoping the overview RPC to the building's org
   * keeps the counts correct regardless. */
  orgId: string;
  /** Open a project (the building dashboard navigates to its Project Dashboard). */
  onProjectSelect: (project: BuildingProject) => void;
}

/**
 * The dashboard's Projects section: an uppercase label row (with a quiet
 * "See all (N) →" link once more projects exist than the grid shows, the
 * archive toggle and the [+ New project] button) above a compact grid of up to
 * 12 most-recently-edited projects. The tiles sit directly on the white page;
 * there is no panel behind the grid (each tile is its own white card).
 *
 * Tiles keep a fixed height and share the row width evenly; a wide screen gains
 * columns rather than stretching each tile indefinitely.
 *
 * Also hosts the project archive UI (MODIFY_DATA only): "Delete project" in the
 * edit dialog soft-archives, and the "Show archived" toggle swaps the grid for
 * restore-only cards — mirroring the buildings archive flow.
 *
 * Distinct from ProjectsSection (which the PM pages reuse purely as a
 * zero-projects empty state), so the dashboard redesign never alters that
 * surface. Per-project doc/member counts come from the org-projects-overview
 * RPC (scoped to the building's own org, filtered to this building — see the
 * orgId prop); the list + edited-order come from useProjects
 * (the overview RPC has no modified_at). Any project without an overview row —
 * loading, errored, or simply missing from the RPC result — renders "—"
 * (never a fake 0); RPC errors are reported to Sentry inside
 * useOrgProjectsOverview.
 */
/** Newest-edited first (fall back to created_at when modified_at is missing),
 *  capped at the dashboard limit unless expanded via "See all". */
function useRecentProjects(projects: BuildingProject[], showAll: boolean) {
  return useMemo(() => {
    const editedTime = (p: BuildingProject) => new Date(p.modified_at || p.created_at).getTime();
    const sorted = [...projects].sort((a, b) => editedTime(b) - editedTime(a));
    return showAll ? sorted : sorted.slice(0, DASHBOARD_PROJECT_LIMIT);
  }, [projects, showAll]);
}

/**
 * The dashboard's own status filter. Unlike the pickers' it opens on All and
 * offers a status only when some project has it; with every project in one
 * status there is nothing to split, so it offers nothing (`options` empty).
 * A chosen status that empties out (its last project changed status) falls
 * back to All rather than leaving an empty grid behind a vanished option.
 */
function useDashboardStatusFilter(projects: BuildingProject[]) {
  const enabled = useProjectStatusEnabled();
  const [chosen, setChosen] = useState<ProjectStatusFilterValue>("all");

  return useMemo(() => {
    const { active, inactive } = partitionProjectsByStatus(projects);
    const counts = { active: active.length, inactive: inactive.length, all: projects.length };
    const split = enabled && active.length > 0 && inactive.length > 0;
    const options: ProjectStatusFilterValue[] = split ? ["all", "active", "inactive"] : [];
    const filter: ProjectStatusFilterValue = options.includes(chosen) ? chosen : "all";
    const shown = filter === "all" ? projects : filter === "active" ? active : inactive;
    return { filter, setFilter: setChosen, options, counts, shown };
  }, [projects, enabled, chosen]);
}

/** projectId (number) → counts, for this building only. */
function useProjectCounts(rows: OrgProjectOverview[], buildingId: string) {
  return useMemo(() => {
    const map = new Map<number, ProjectCounts>();
    const buildingIdNum = Number(buildingId);
    for (const row of rows) {
      if (row.buildingId !== buildingIdNum) continue;
      map.set(row.projectId, {
        docCount: row.docCount,
        memberCount: row.memberCount,
        issueCount: row.issueCount,
      });
    }
    return map;
  }, [rows, buildingId]);
}

/** "Show archived (N)" / "Show active (N)" switch — one of the label row's
 *  right-aligned controls, mirroring the buildings active/archived toggle.
 *  Renders nothing until there's an archive to reveal (or the archived view is
 *  already open, so the way back never disappears), and never for users
 *  without MODIFY_DATA. */
function ArchiveToggle(props: {
  showArchived: boolean;
  activeCount: number;
  archivedCount: number;
  onToggle: () => void;
}) {
  const { showArchived, activeCount, archivedCount, onToggle } = props;
  const { canArchiveProjects } = usePermissions();
  const { t } = useLanguage();
  if (!canArchiveProjects || (archivedCount === 0 && !showArchived)) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
      onClick={onToggle}
    >
      {showArchived
        ? `${t.projects.archive.showActive} (${activeCount})`
        : `${t.projects.archive.showArchived} (${archivedCount})`}
    </Button>
  );
}

interface ProjectsSubGridProps {
  buildingId: string;
  countsByProject: Map<number, ProjectCounts>;
  onProjectSelect: (project: BuildingProject) => void;
  onEdit: (project: BuildingProject) => void;
  /** Bypass the recent-projects cap (title-row "See all" toggle). */
  showAll: boolean;
  /** The projects the label row's status filter leaves in view. */
  shownProjects: BuildingProject[];
}

/** The default (active-projects) view: recent projects only — creating one is
 *  the label row's [+ New project] button, so with no projects yet the grid is
 *  replaced by a note pointing at it. */
function ActiveProjectsGrid({
  buildingId,
  countsByProject,
  onProjectSelect,
  onEdit,
  showAll,
  shownProjects,
}: ProjectsSubGridProps) {
  const { t } = useLanguage();
  const { projects, setProjectStatus, pendingStatusProjectIds } = useProjects(buildingId);
  const { canModifyProjects, canManageProjectStatus } = usePermissions();
  const recentProjects = useRecentProjects(shownProjects, showAll);

  if (projects.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">{t.projects.empty.title}</p>
    );
  }

  return (
    <div className={gridClass}>
      {recentProjects.map((project) => (
        <DashboardProjectCard
          key={project.id}
          project={project}
          counts={countsByProject.get(Number(project.id))}
          onSelect={() => onProjectSelect(project)}
          onEdit={canModifyProjects ? () => onEdit(project) : undefined}
          isSettingStatus={pendingStatusProjectIds.has(project.id)}
          onSetStatus={
            canManageProjectStatus
              ? (status) => setProjectStatus({ id: project.id, status })
              : undefined
          }
        />
      ))}
    </div>
  );
}

/** The archived-projects view: restore-only cards (uncapped — this is the
 *  recovery surface, and no other page lists archived projects), or an empty
 *  note once the last one is restored while the view is still open. */
function ArchivedProjectsGrid(props: {
  buildingId: string;
  countsByProject: Map<number, ProjectCounts>;
}) {
  const { buildingId, countsByProject } = props;
  const { t } = useLanguage();
  const { archivedProjects, archiveProject, isArchiving } = useProjects(buildingId);
  const { canArchiveProjects } = usePermissions();

  if (archivedProjects.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        {t.projects.archive.noArchived}
      </p>
    );
  }
  return (
    <div className={gridClass}>
      {archivedProjects.map((project) => (
        <ArchivedProjectCard
          key={project.id}
          project={project}
          counts={countsByProject.get(Number(project.id))}
          canRestore={canArchiveProjects}
          restoreDisabled={isArchiving}
          onRestore={() => archiveProject({ id: project.id, archived: false })}
        />
      ))}
    </div>
  );
}

export function DashboardProjectsGrid({
  buildingId,
  orgId,
  onProjectSelect,
}: DashboardProjectsGridProps) {
  const { t } = useLanguage();
  const { projects, archivedProjects } = useProjects(buildingId);
  const { canCreateProjects } = usePermissions();
  const overview = useOrgProjectsOverview(orgId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<BuildingProject | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);

  const countsByProject = useProjectCounts(overview.rows, buildingId);
  const statusFilter = useDashboardStatusFilter(projects);
  const shownProjects = statusFilter.shown;

  return (
    <div>
      {/* Label row — the uppercase section label with its controls
          right-aligned. The
          "See all (N) →" toggle appears only when projects overflow the grid,
          and only in the active view — in the archived view it would point
          away from the cards on screen. It expands the grid in place; the
          standalone folders/projects page is gone. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <SettingsSectionLabel>{t.buildingDetail.dashboard.projects}</SettingsSectionLabel>
          {!showArchived && statusFilter.options.length > 0 && (
            <ProjectStatusFilter
              variant="inline"
              options={statusFilter.options}
              value={statusFilter.filter}
              onValueChange={statusFilter.setFilter}
              counts={statusFilter.counts}
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          {!showArchived && shownProjects.length > DASHBOARD_PROJECT_LIMIT && (
            <button
              type="button"
              onClick={() => setShowAllProjects((prev) => !prev)}
              className="text-xs text-muted-foreground transition-colors hover:text-primary"
            >
              {showAllProjects
                ? t.projects.card.showFewer
                : `${t.projects.card.seeAll} (${shownProjects.length}) →`}
            </button>
          )}
          <ArchiveToggle
            showArchived={showArchived}
            activeCount={projects.length}
            archivedCount={archivedProjects.length}
            onToggle={() => setShowArchived((prev) => !prev)}
          />
          {!showArchived && canCreateProjects && (
            <NewProjectButton onClick={() => setIsCreateOpen(true)} />
          )}
        </div>
      </div>

      <div className="mt-3">
        {showArchived ? (
          <ArchivedProjectsGrid buildingId={buildingId} countsByProject={countsByProject} />
        ) : (
          <ActiveProjectsGrid
            buildingId={buildingId}
            countsByProject={countsByProject}
            onProjectSelect={onProjectSelect}
            onEdit={setEditingProject}
            showAll={showAllProjects}
            shownProjects={shownProjects}
          />
        )}
      </div>

      <CreateProjectDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        buildingId={buildingId}
      />
      {editingProject && (
        <EditProjectDialog
          key={editingProject.id}
          buildingId={buildingId}
          project={editingProject}
          onClose={() => setEditingProject(null)}
        />
      )}
    </div>
  );
}
